import uniqBy from "lodash/uniqBy";

import { WHITE_COLOR_RGB } from "@stadium/constants/color";
import {
  PREVIEWABLE_FORMATS,
  SVG_FORMAT_KEYS,
  LOGO_FORMAT_PRECEDENCE,
  FORMAT_MAP,
} from "@stadium/constants/logo";

import { ColorThief } from "@stadium/services/color-thief";

import { Color } from "./color";

export const isSvgFormat = (format) => SVG_FORMAT_KEYS.includes(format);

export const getFileExtension = (src) => {
  if (typeof src === "string") {
    const pattern = /\.([a-zA-Z0-9]+)(?=\?|$)/;
    const match = pattern.exec(src);
    return match ? match[1] : "";
  }
  return "";
};

export const getAssetContentType = async (src) => {
  const response = await fetch(src);
  const contentType = response.headers.get("content-type");
  if (!contentType) {
    return null;
  }
  return contentType;
};

export const isPreviewableType = (type) => PREVIEWABLE_FORMATS.includes(type);

export const getLogoFormatName = (format, name) => {
  const extension = getFileExtension(name);
  if (extension) {
    return extension.toLowerCase();
  }

  return FORMAT_MAP[format?.toLowerCase()] || format?.toLowerCase() || "";
};

export const getLogoColorConfig = (data) => {
  return new Promise((resolve, reject) => {
    const colorThief = new ColorThief();
    const image = new Image();
    image.src = data;
    image.crossOrigin = "anonymous";

    image.onload = () => {
      try {
        /**
         * colorthief will return empty color pallet for a Whole white image.
         * Ref: https://github.com/lokesh/color-thief/issues/72
         */
        const colorPallete = colorThief.getPalette(image);

        const uniqColorPallete = uniqBy(colorPallete, (val) => `${val[0]},${val[1]},${val[2]}`);

        // Manually adding white if color pallet is empty
        const dominantColor =
          uniqColorPallete.length == 0 ? WHITE_COLOR_RGB : colorThief.getColor(image);

        resolve({
          colorPallete: uniqColorPallete.map((color) => new Color(color)),
          dominantColor: new Color(dominantColor),
        });
      } catch (err) {
        reject(new Error(err));
      }
    };

    image.onerror = reject;
  });
};

export const sortLogosByPrecedence = (logos) => {
  return logos.sort(
    ({ format: firstFormat }, { format: secondFormat }) =>
      LOGO_FORMAT_PRECEDENCE[firstFormat] - LOGO_FORMAT_PRECEDENCE[secondFormat],
  );
};

export const extractDomainName = (input) => {
  if (!input) return "";

  let url = input.trim();

  url = url.replace(/^https?(?=\/\/)/i, (match) => match + ":");
  if (!/^https?:\/\//i.test(url)) {
    url = "https://" + url;
  }

  try {
    const hostname = new URL(url).hostname;
    const parts = hostname.replace(/^www\./, "").split(".");
    return parts[0];
  } catch {
    return "";
  }
};
